import React, { createContext, useContext, useState } from "react";

import "../../style/menubar.module.css";

type MenuContextValue = {
    isMenuOpen: boolean;
    closeMenu: () => void;
    openMenu: () => void;
};

const MenubarContext = createContext<MenuContextValue | undefined>(undefined);

function useMenubarContext() {
    const context = useContext(MenubarContext);

    if (context === undefined) {
        throw Error("useMenubarContext is used outside the MenubarContext provider.");
    }

    return context;
}

type MenubarProps = {
    children: React.ReactNode;
    className?:string
};

export function Menubar(props: MenubarProps) {
    const { children, className= "" } = props;

    const [isMenuOpen, setIsShowMenu] = useState(false);

    function closeMenu() {
        setIsShowMenu(false);
    }

    function openMenu() {
        setIsShowMenu(true);
    }

    return (
        <MenubarContext.Provider value={{ isMenuOpen, closeMenu, openMenu }}>
            <div onMouseLeave={closeMenu} className={`menubar ${className}`}>
                {children}
            </div>
        </MenubarContext.Provider>
    );
}

type MenubarTriggerProps = {
    children: React.ReactPortal | React.ReactElement<unknown, string | React.JSXElementConstructor<any>>;
};

export function MenubarTrigger(props: MenubarTriggerProps) {
    const { children } = props;

    const { openMenu } = useMenubarContext();

    return React.cloneElement(children, {
        ...children.props,
        onMouseEnter: () => openMenu()
    });
}

type MenuContentProps = {
    children: React.ReactNode;
    position?: Partial<{
        top: string;
        bottom: string;
        left: string;
        right: string;
    }>;
};

export default function MenuContent(props: MenuContentProps) {
    const { isMenuOpen } = useMenubarContext();

    if (isMenuOpen === false) {
        return null;
    }

    return (
        <>
            <div style={props.position} className="menubar-content-wrapper">
                <div className="menubar-content">{props.children}</div>
            </div>
        </>
    );
}

type MenuItemActionType = "regular" |"disabled" | "danger";

type MenubarItemProps = {
    children: React.ReactNode;
    type: MenuItemActionType;
} & Pick<JSX.IntrinsicElements["div"], "className" | "onClick">;

export function MenubarItem(props: MenubarItemProps) {
    const { children, className = "", type } = props;
    const { closeMenu } = useMenubarContext();

    return (
        <div
            {...props}
            onClick={e => {
                if (props.type !== "disabled") {
                    if (props.onClick !== undefined) {
                        props.onClick(e);
                    }
                    closeMenu();
                }
            }}
            className={`menubar-item ${actionTypeToCssMapper(type)} ${className}`}
        >
            {children}
        </div>
    );
}

function actionTypeToCssMapper(type: MenuItemActionType) {
    switch (type) {
        case "disabled":
            return "menu-disabled-action";
        case "danger":
            return "menu-danger-action";
        case "regular":
            return "menu-regular-action";
        default:
            return "";
    }
}
